import React from "react";
import { useTranslation } from "react-i18next";

interface PrePublishHeaderProps {
  title: string;
  selectedDatesCount: number;
  timeSlotsCount: number;
  participantEmailsCount: number;
  /** Layout compact mobile : "X dates · Y créneaux" sans "invités potentiels" */
  compact?: boolean;
}

/**
 * Header du PrePublishModal — dot violet + label uppercase brand +
 * titre du sondage + sub-counters. Cf. Pre-publish Modal B.html lignes 96-108.
 */
const PrePublishHeader: React.FC<PrePublishHeaderProps> = ({
  title,
  selectedDatesCount,
  timeSlotsCount,
  participantEmailsCount,
  compact = false,
}) => {
  const { t } = useTranslation("polls");
  const datesLabel = compact
    ? t("prepublish_dates_compact", { count: selectedDatesCount })
    : t("prepublish_dates_full", { count: selectedDatesCount });
  const slotsLabel = t("prepublish_slots", { count: timeSlotsCount });
  const invitesLabel = compact
    ? null
    : t("prepublish_invites", { count: participantEmailsCount });

  return (
    <div data-testid="pre-publish-header" className="space-y-1">
      <div className="flex items-center gap-1.5">
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="currentColor"
          className="text-violet-400"
          aria-hidden
        >
          <circle cx="5" cy="5" r="5" />
        </svg>
        <span
          data-testid="pre-publish-label"
          className="text-[11px] font-bold uppercase tracking-[0.6px] text-violet-400"
        >
          {compact ? t("prepublish_label_compact") : t("prepublish_label_full")}
        </span>
      </div>

      <h2
        data-testid="pre-publish-title"
        className={`font-semibold tracking-tight text-foreground ${
          compact ? "text-[16px]" : "text-[18px]"
        }`}
      >
        {title || "Sondage"}
      </h2>

      <div
        data-testid="pre-publish-counters"
        className="text-xs text-muted-foreground"
      >
        {datesLabel}
        <span className="mx-1.5">·</span>
        {slotsLabel}
        {invitesLabel && (
          <>
            <span className="mx-1.5">·</span>
            {invitesLabel}
          </>
        )}
      </div>
    </div>
  );
};

export default PrePublishHeader;
